/**
 * Everybody using this, and what state they are in. F-81.
 *
 * One table, one row per learner, every column a thing the operator would otherwise
 * have to ask a person or an AWS CLI: what they are learning, whether a question is
 * open, whether they are paused, when they last said anything, and what they have
 * cost. It is deliberately dense — this is the screen he opens to answer "is anyone
 * stuck", and a card-per-learner layout would answer it one scroll at a time.
 *
 * English-only, no `t()`. See the note at the top of `Console.tsx`.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  isError,
  listLearners,
  revealPhone,
  type AdminLearner,
  type AdminUsage,
  type ApiError,
} from './api';
import { Failed, Id, Nil, Waiting, abbreviate, ago, dollars, localTitle } from './parts';

/**
 * What a web search costs, from docs/costs.md: `$10/1,000`.
 *
 * The only part of model spend this screen can price honestly. The usage counters
 * are aggregates across every model the account touched — Opus research, Sonnet item
 * generation, Haiku grading — and a token is a different price in each, so turning
 * `inputTokens` into dollars here would mean inventing a blended rate and printing
 * it as though it were measured. Searches are a flat per-search charge whatever the
 * model, so they are exact.
 *
 * The gap is real and it is F-81's "month-to-date model spend": see the report. It
 * closes with per-model counters, on the API side, not with arithmetic here.
 */
const DOLLARS_PER_SEARCH = 0.01;

/** A counter that may not have been written yet. Zero is the honest reading of absent. */
const counted = (value: number | undefined) => (typeof value === 'number' ? value : 0);

/** Tokens, all six counters, as one number worth glancing at plus the breakdown on hover. */
function Usage({ usage }: { usage: AdminUsage | undefined }) {
  if (!usage) return <Nil />;
  const parts: [string, number][] = [
    ['in', counted(usage.inputTokens)],
    ['out', counted(usage.outputTokens)],
    ['cache read', counted(usage.cacheReadTokens)],
    ['cache write', counted(usage.cacheWriteTokens)],
    ['batch in', counted(usage.batchInputTokens)],
    ['batch out', counted(usage.batchOutputTokens)],
  ];
  const total = parts.reduce((sum, [, value]) => sum + value, 0);
  const searches = counted(usage.webSearches);

  return (
    <span
      className="stack"
      title={parts.map(([label, value]) => `${label} ${value.toLocaleString('en')}`).join('\n')}
    >
      <span className="num">{abbreviate(total)} tok</span>
      <span className="sub">
        {counted(usage.calls)} calls · {searches} searches
        {searches > 0 && ` ≈ ${dollars(searches * DOLLARS_PER_SEARCH)}`}
      </span>
    </span>
  );
}

/**
 * A phone number, masked, with one deliberate way to see it (F-81).
 *
 * The reveal is a request, not a toggle over a value the page already holds: the
 * server logs the read (D-21), which it cannot do if the number arrived with the
 * list. Revealing on this screen therefore costs a round trip, which is the correct
 * price for it.
 *
 * It hides itself again rather than staying open, and nothing persists it across a
 * reload. A number revealed once and left on screen is a number readable over a
 * shoulder for the rest of the afternoon, which is the failure the masking is for.
 */
function Phone({ learner }: { learner: AdminLearner }) {
  const [full, setFull] = useState<string>();
  const [failed, setFailed] = useState<string>();
  const [asking, setAsking] = useState(false);

  if (full) {
    return (
      <span className="stack">
        <Id value={full} />
        <button type="button" className="admin-link" onClick={() => setFull(undefined)}>
          hide
        </button>
      </span>
    );
  }

  return (
    <span className="stack">
      <Id value={learner.phoneMasked} />
      <button
        type="button"
        className="admin-link"
        disabled={asking}
        onClick={async () => {
          setAsking(true);
          setFailed(undefined);
          const result = await revealPhone(learner.userId);
          setAsking(false);
          if (isError(result)) setFailed(`${result.error} · ${result.status}`);
          else setFull(result.phone);
        }}
      >
        {asking ? 'revealing…' : 'reveal'}
      </button>
      {failed && <span className="sub bad">{failed}</span>}
    </span>
  );
}

/** Whether a question is out and unanswered, and for how long — the stuck-learner column. */
function Open({ learner }: { learner: AdminLearner }) {
  if (!learner.openItemId) return <span className="sub">none open</span>;
  return (
    <span className="stack">
      <Id value={learner.openItemId} />
      <span className="sub">open {ago(learner.openSince)}</span>
    </span>
  );
}

export default function Learners() {
  const [learners, setLearners] = useState<AdminLearner[]>();
  const [error, setError] = useState<ApiError>();

  useEffect(() => {
    let stale = false;
    void listLearners().then((result) => {
      if (stale) return;
      if (isError(result)) setError(result);
      else setLearners(result.learners);
    });
    return () => {
      stale = true;
    };
  }, []);

  if (error) return <Failed error={error} what="Learners" />;
  if (!learners) return <Waiting what="learners" />;

  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h1>Learners</h1>
        <p className="sub">{learners.length} rows</p>
      </header>

      {learners.length === 0 ? (
        <p className="admin-empty">Nobody has signed in yet.</p>
      ) : (
        <div className="admin-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Learner</th>
                <th>Phone</th>
                <th>Locale / zone</th>
                <th>Objective</th>
                <th>Window</th>
                <th>Open</th>
                <th>Reminders</th>
                <th>Last in</th>
                <th>Last out</th>
                <th>Usage</th>
              </tr>
            </thead>
            <tbody>
              {learners.map((learner) => (
                <tr key={learner.userId} className={learner.paused ? 'is-paused' : undefined}>
                  <td>
                    <span className="stack">
                      <Link className="admin-link strong" to={`/admin/learner/${learner.userId}`}>
                        {learner.name || '(no name)'}
                      </Link>
                      <Id value={learner.userId} />
                      {learner.paused && <span className="tag warn">paused</span>}
                    </span>
                  </td>
                  <td>
                    <Phone learner={learner} />
                  </td>
                  <td>
                    <span className="stack">
                      <span>{learner.locale}</span>
                      <span className="sub">{learner.timezone}</span>
                    </span>
                  </td>
                  <td>
                    {learner.objective ? (
                      <span className="stack">
                        <Link
                          className="admin-link"
                          to={`/admin/objective/${learner.objective.objectiveId}`}
                        >
                          {localTitle(learner.objective.title)}
                        </Link>
                        <span className="sub">
                          <span className={`tag status-${learner.objective.status}`}>
                            {learner.objective.status}
                          </span>{' '}
                          {learner.objective.activatedAt
                            ? `started ${ago(learner.objective.activatedAt)}`
                            : 'never started'}
                        </span>
                      </span>
                    ) : (
                      <span className="sub">no objective</span>
                    )}
                  </td>
                  <td>
                    {learner.window ? (
                      <span className="stack">
                        <span className="num">
                          {learner.window.reachFrom}–{learner.window.reachUntil}
                        </span>
                        <span className="sub">
                          {learner.window.dailyLimit}/day
                          {learner.window.explanations ? ' · explanations' : ''}
                        </span>
                      </span>
                    ) : (
                      <span className="sub">unset</span>
                    )}
                  </td>
                  <td>
                    <Open learner={learner} />
                  </td>
                  <td className="num">{learner.reminders}</td>
                  <td className="num" title={learner.lastInboundAt ?? undefined}>
                    {ago(learner.lastInboundAt)}
                  </td>
                  <td className="num" title={learner.lastOutboundAt ?? undefined}>
                    {ago(learner.lastOutboundAt)}
                  </td>
                  <td>
                    <Usage usage={learner.usage} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
